import React, { useState, useEffect } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { Save, X, Smile, Star, MapPin, Clock, Lock, Users } from "lucide-react";

const MOODS = [
  { v: "ansioso",      label: "ansioso" },
  { v: "curioso",      label: "curioso" },
  { v: "calmo",        label: "calmo" },
  { v: "entusiasmado", label: "entusiasmado" },
  { v: "cansado",      label: "cansado" },
  { v: "romantico",    label: "romântico" },
  { v: "neutro",       label: "neutro" },
];

const today = () => new Date().toISOString().slice(0, 10);

const EncounterForm = ({ matchId, initial, onSaved, onCancel }) => {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    date: initial?.date || today(),
    location: initial?.location || "",
    duration_minutes: initial?.duration_minutes ?? "",
    mood_before: initial?.mood_before || "",
    mood_after: initial?.mood_after || "",
    rating: initial?.rating || 0,
    would_meet_again: initial?.would_meet_again ?? null,
    notes: initial?.notes || "",
    visibility: initial?.visibility || "private",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm((f) => ({ ...f, ...initial, duration_minutes: initial.duration_minutes ?? "" }));
    }
  }, [initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        match_id: matchId,
        date: form.date,
        location: form.location,
        duration_minutes: form.duration_minutes === "" ? null : Number(form.duration_minutes),
        mood_before: form.mood_before || null,
        mood_after: form.mood_after || null,
        rating: form.rating || null,
        would_meet_again: form.would_meet_again,
        notes: form.notes,
        visibility: form.visibility,
      };
      const res = isEdit
        ? await client.put(`/encounters/${initial.id}`, payload)
        : await client.post("/encounters", payload);
      toast.success(isEdit ? "Registo actualizado." : "Encontro registado.");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha a guardar.");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#0C0E12] border border-white/10 rounded-2xl p-6 space-y-5" data-testid="encounter-form">
      <div className="flex items-start justify-between">
        <div>
          <span className="label-uppercase">Registar encontro</span>
          <h3 className="font-display text-2xl mt-2">
            {isEdit ? "Editar memória" : "Nova memória"}
          </h3>
        </div>
        <button onClick={onCancel} className="text-[#A29F9B] hover:text-white" data-testid="encounter-form-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label-uppercase">Data</label>
          <input type="date" value={form.date} onChange={(e)=>set("date", e.target.value)}
            className="input-elegant mt-2" data-testid="encounter-date" />
        </div>
        <div>
          <label className="label-uppercase">Duração (minutos)</label>
          <div className="mt-2 flex items-center gap-3 bg-[#161920] border border-white/5 rounded-2xl px-4 py-3">
            <Clock className="w-4 h-4 text-[#C5A880] shrink-0" />
            <input type="number" min="0" step="15"
              value={form.duration_minutes}
              onChange={(e)=>set("duration_minutes", e.target.value)}
              placeholder="90"
              className="bg-transparent border-0 outline-none flex-1 text-[#F5F2EE] text-sm placeholder:text-[#A29F9B]"
              data-testid="encounter-duration" />
          </div>
        </div>
      </div>

      <div>
        <label className="label-uppercase">Local</label>
        <div className="mt-2 flex items-center gap-3 bg-[#161920] border border-white/5 rounded-2xl px-4 py-3">
          <MapPin className="w-4 h-4 text-[#C5A880] shrink-0" />
          <input
            value={form.location} onChange={(e)=>set("location", e.target.value)}
            placeholder="Café Nicola, passeio marítimo, casa..."
            className="bg-transparent border-0 outline-none flex-1 text-[#F5F2EE] text-sm placeholder:text-[#A29F9B]"
            data-testid="encounter-location" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <MoodPicker label="Como chegaste" value={form.mood_before} onChange={(v)=>set("mood_before", v)} testidPrefix="encounter-mood-before" />
        <MoodPicker label="Como saíste"    value={form.mood_after}  onChange={(v)=>set("mood_after", v)}  testidPrefix="encounter-mood-after" />
      </div>

      <div>
        <label className="label-uppercase">Avaliação do encontro</label>
        <div className="mt-2 flex items-center gap-2" data-testid="encounter-rating">
          {[1,2,3,4,5].map((n) => (
            <button key={n} type="button" onClick={()=>set("rating", form.rating === n ? 0 : n)}
              className={`p-1 rounded-full transition-transform hover:scale-110 ${form.rating >= n ? "text-[#C5A880]" : "text-white/20"}`}
              data-testid={`encounter-rating-${n}`}>
              <Star className="w-6 h-6" fill={form.rating >= n ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label-uppercase">Voltarias a sair?</label>
        <div className="mt-2 flex gap-2" data-testid="encounter-meet-again">
          {[
            { v: true,  label: "Sim" },
            { v: false, label: "Não" },
            { v: null,  label: "Indeciso" },
          ].map((opt) => (
            <button key={String(opt.v)} type="button"
              onClick={()=>set("would_meet_again", opt.v)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                form.would_meet_again === opt.v
                  ? "bg-[#C5A880]/15 border-[#C5A880] text-[#C5A880]"
                  : "bg-transparent border-white/10 text-[#DCD7D2] hover:border-white/30"
              }`}
              data-testid={`encounter-meet-${String(opt.v)}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="label-uppercase">Notas</label>
        <textarea rows={4} value={form.notes} onChange={(e)=>set("notes", e.target.value)}
          placeholder="O que me surpreendeu, o momento que quero lembrar, aquilo que me deixou a pensar..."
          className="w-full bg-[#161920] border border-white/5 rounded-2xl p-4 mt-2 text-[#F5F2EE] outline-none focus:border-[#C5A880]/50 text-sm"
          data-testid="encounter-notes" />
      </div>

      <div>
        <label className="label-uppercase">Visibilidade</label>
        <div className="mt-2 grid sm:grid-cols-2 gap-3" data-testid="encounter-visibility">
          <button type="button" onClick={()=>set("visibility", "private")}
            className={`flex items-start gap-3 text-left p-4 rounded-2xl border transition-colors ${
              form.visibility === "private"
                ? "bg-[#C5A880]/10 border-[#C5A880]"
                : "bg-[#161920] border-white/5 hover:border-white/15"
            }`}
            data-testid="encounter-visibility-private">
            <Lock className="w-4 h-4 text-[#C5A880] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-[#F5F2EE]">Privado</div>
              <div className="text-xs text-[#A29F9B] mt-0.5">Só tu vês. Memória íntima.</div>
            </div>
          </button>
          <button type="button" onClick={()=>set("visibility", "shared")}
            className={`flex items-start gap-3 text-left p-4 rounded-2xl border transition-colors ${
              form.visibility === "shared"
                ? "bg-[#C5A880]/10 border-[#C5A880]"
                : "bg-[#161920] border-white/5 hover:border-white/15"
            }`}
            data-testid="encounter-visibility-shared">
            <Users className="w-4 h-4 text-[#C5A880] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-[#F5F2EE]">Partilhado</div>
              <div className="text-xs text-[#A29F9B] mt-0.5">Visível para ambos. Diário conjunto.</div>
            </div>
          </button>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} className="btn-secondary" data-testid="encounter-cancel">Cancelar</button>
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="encounter-save">
          <Save className="w-4 h-4" /> {saving ? "A guardar..." : "Guardar"}
        </button>
      </div>
    </div>
  );
};

const MoodPicker = ({ label, value, onChange, testidPrefix }) => (
  <div>
    <label className="label-uppercase">{label}</label>
    <div className="mt-2 flex flex-wrap gap-2" data-testid={testidPrefix}>
      {MOODS.map((m) => (
        <button key={m.v} type="button"
          onClick={()=>onChange(value === m.v ? "" : m.v)}
          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
            value === m.v
              ? "bg-[#C5A880]/15 border-[#C5A880] text-[#C5A880]"
              : "bg-transparent border-white/10 text-[#DCD7D2] hover:border-white/30"
          }`}
          data-testid={`${testidPrefix}-${m.v}`}>
          <Smile className="w-3 h-3 inline mr-1.5 -mt-0.5" />{m.label}
        </button>
      ))}
    </div>
  </div>
);

export default EncounterForm;
